/**
 * <TutorStudentBubbles> — les élèves du live au téléphone : une rangée de bulles, moi au bout
 * (molecule).
 *
 * La pellicule n'a pas la place d'une colonne sur un téléphone : chaque élève est une bulle vidéo
 * **cerclée de son mode** (le vert de Dialogue, le violet de Concentré), son prénom et son mode
 * dessous, la main levée à côté du prénom ; l'élève regardé porte en plus l'anneau de l'univers. Ma
 * bulle ferme la rangée (« Micro ouvert », « Micro coupé », « Tu présentes »). La rangée défile quand
 * elle déborde.
 *
 * Toucher une bulle regarde l'élève : sa bascule de mode et sa liste sont au centre (la bascule en
 * tête, la liste dans l'onglet Activités). Changer le mode d'un élève qu'on ne regarde pas prend donc
 * deux gestes (Wajih, 02/11). Contrôlée : l'élève regardé arrive en prop.
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UILiveParticipant } from "../../../ports/primary/ui/types/studentLive"
import { tutorPendingLabel } from "../../../ports/primary/ui/formatters/tutorSessionFormatter"
import { type UITutorLiveStudent } from "../../../ports/primary/ui/types/tutorLive"
import { Row } from "../../atoms/containers/Row"
import { HorizontalList } from "../../atoms/HorizontalList"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { Badge } from "../../atoms/Badge"
import { Text } from "../../atoms/typography/Text"
import { textInkOnFill, type SemanticColorKey } from "../../theme/tokens/colors"
import { SOUND_MODE_LABELS } from "../SoundModeBadge"
import { tutorStudentStateChip } from "../TutorStudentChips/tutorStudentFaces"
import { tutorMeParticipant, tutorStudentParticipant } from "../TutorStudentVideo"
import { VideoTile } from "../VideoTile"
import { cappedCount } from "../../utils/cappedCount"

export type TutorStudentBubblesProps = {
    students: UITutorLiveStudent[]
    selectedId?: string
    onSelect: (studentId: string) => void
    me: UILiveParticipant
    /** Je présente le centre (au web seulement : le téléphone ne présente pas). Défaut `false`. */
    presenting?: boolean
    /** La vraie piste d'un participant (`me.id` pour la mienne). */
    renderVideo?: (participantId: string) => React.ReactNode
    testID?: string
}

const TEXTS = {
    me: "Toi",
    micOn: "Micro ouvert",
    micOff: "Micro coupé",
    presenting: "Tu présentes",
    hand: "main levée",
}

const BUBBLE = 56
const RING = 3
const GAP = 2
/** La bulle et ses deux anneaux (le mode, puis la sélection), et un peu d'air pour son prénom. */
const BUBBLE_WIDTH = BUBBLE + 4 * (RING + GAP) + 4

/** L'anneau du mode ; son mot s'écrit à l'encre de cet aplat. */
const MODE_RING: Record<UITutorLiveStudent["soundMode"], SemanticColorKey> = { dialogue: "success", focused: "purple" }

export function TutorStudentBubbles(props: TutorStudentBubblesProps) {
    const { students, selectedId, onSelect, me, presenting = false, renderVideo, testID } = props
    const meVideo = renderVideo?.(me.id)
    const meWord = presenting ? TEXTS.presenting : me.micOff === true ? TEXTS.micOff : TEXTS.micOn

    return (
        <View testID={testID}>
            <HorizontalList gap="3" padding="1">
                {students.map((student) => {
                    const here = student.presence === "here"
                    const mode = MODE_RING[student.soundMode]
                    const selected = student.id === selectedId
                    const hand = student.state === "hand_raised"
                    // Absent : le mot de sa présence, celui de sa puce (« Hors-ligne », « Pas encore là »).
                    const word = here
                        ? SOUND_MODE_LABELS[student.soundMode]
                        : (tutorStudentStateChip(student)?.text ?? "")
                    return (
                        <Pressable
                            key={student.id}
                            onPress={() => onSelect(student.id)}
                            accessibilityRole="button"
                            accessibilityState={{ selected }}
                            accessibilityLabel={[
                                student.firstname,
                                word,
                                hand ? TEXTS.hand : null,
                                (student.pending ?? 0) > 0 && !selected ? tutorPendingLabel(student.pending!) : null,
                            ]
                                .filter(Boolean)
                                .join(", ")}
                            style={styles.bubble}
                        >
                            <View style={styles.select(selected)}>
                                <View style={styles.ring(here ? mode : "border")}>
                                    <VideoTile
                                        participant={tutorStudentParticipant(student)}
                                        size="round"
                                        diameter={BUBBLE}
                                        video={renderVideo?.(student.id)}
                                        absent={!here}
                                    />
                                </View>
                                {(student.pending ?? 0) > 0 && !selected && (
                                    <View style={styles.pending} pointerEvents="none">
                                        <Badge text={cappedCount(student.pending!)} variant="info" />
                                    </View>
                                )}
                            </View>
                            <Row gap="1" align="center">
                                <Text size="text-sm" weight="bold" color="text-strong" numberOfLines={1}>
                                    {student.firstname}
                                </Text>
                                {hand && (
                                    <IconBubble
                                        iconName="hand-paper"
                                        size={18}
                                        color="accent"
                                        iconColor="text-on-accent"
                                    />
                                )}
                            </Row>
                            <Text
                                size="text-xs"
                                weight="bold"
                                color={here ? textInkOnFill(mode) : "text-muted"}
                                numberOfLines={1}
                            >
                                {word}
                            </Text>
                        </Pressable>
                    )
                })}
                <View style={styles.bubble} accessible accessibilityLabel={`${TEXTS.me}, ${meWord}`}>
                    <View style={styles.select(false)}>
                        <View style={styles.ring(presenting ? "role" : "border")}>
                            <VideoTile
                                participant={tutorMeParticipant(me, meVideo)}
                                size="round"
                                diameter={BUBBLE}
                                me
                                video={meVideo}
                            />
                        </View>
                    </View>
                    <Text size="text-sm" weight="bold" color="text-strong">
                        {TEXTS.me}
                    </Text>
                    <Text size="text-xs" weight="bold" color="text-muted" numberOfLines={1}>
                        {meWord}
                    </Text>
                </View>
            </HorizontalList>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Ce qui attend le masteur dans la session de l'élève : son compte, comme sur la vignette. */
    pending: { position: "absolute" as const, top: 0, right: 0 },
    bubble: { width: BUBBLE_WIDTH, alignItems: "center" as const, gap: 2 },
    /** L'élève regardé : un second anneau, à l'univers, autour de celui de son mode. */
    select: (selected: boolean) => ({
        padding: GAP,
        borderRadius: theme.radius.full,
        borderWidth: RING,
        borderColor: selected ? theme.colors.role : "transparent",
    }),
    ring: (color: SemanticColorKey) => ({
        padding: GAP,
        borderRadius: theme.radius.full,
        borderWidth: RING,
        borderColor: theme.colors[color],
    }),
}))
